import { useState, useMemo } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Clutch, Egg, CreateEggDto } from '../api/clutches';
import { EggStatus, ChickRearMethod } from '../utils/enums';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { clutchesApi } from '../api/clutches';
import { getEggDefaultsFromClutch } from '../utils/eggSpeciesDefaults';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';

interface ManageEggsModalProps {
  isOpen: boolean;
  onClose: () => void;
  clutch: Clutch;
  onUpdate: () => Promise<void>;
}

const eggSchema = z.object({
  layDate: z.string(),
  status: z.nativeEnum(EggStatus),
  incubationStartDate: z.string().optional(),
  incubationDays: z.number().min(0).max(50).optional(),
  bandingNotificationDays: z.number().min(0).max(50).optional(),
  comment: z.string().optional(),
  chickRearMethod: z.nativeEnum(ChickRearMethod).optional(),
  hatchingDate: z.string().optional(),
  bandingDate: z.string().optional(),
  bandNumber: z.string().optional(),
  failureReason: z.string().optional(),
});

type EggFormData = z.infer<typeof eggSchema>;

export default function ManageEggsModal({
  isOpen,
  onClose,
  clutch,
  onUpdate,
}: ManageEggsModalProps) {
  const [isAddingEgg, setIsAddingEgg] = useState(false);
  const [editingEgg, setEditingEgg] = useState<Egg | null>(null);
  const speciesEggDefaults = useMemo(() => getEggDefaultsFromClutch(clutch), [clutch]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<EggFormData>({
    resolver: zodResolver(eggSchema),
    defaultValues: {
      layDate: format(new Date(), 'yyyy-MM-dd'),
      status: EggStatus.LAID,
      chickRearMethod: ChickRearMethod.PARENT_REARED,
      incubationDays: speciesEggDefaults.incubationDays,
      bandingNotificationDays: speciesEggDefaults.bandingNotificationDays,
    },
  });

  const handleAddEgg = async (data: EggFormData) => {
    try {
      const newEgg: CreateEggDto = {
        id: crypto.randomUUID(),
        clutchId: clutch.id,
        status: data.status,
        layDate: data.layDate,
        incubationStartDate: data.incubationStartDate || null,
        incubationDays: data.incubationDays ?? speciesEggDefaults.incubationDays,
        bandingNotificationDays: data.bandingNotificationDays ?? speciesEggDefaults.bandingNotificationDays,
        comment: data.comment || null,
        chickRearMethod: data.chickRearMethod || ChickRearMethod.PARENT_REARED,
        hatchingDate: data.hatchingDate || null,
        bandingDate: data.bandingDate || null,
        bandNumber: data.bandNumber || null,
        failureReason: data.failureReason || null,
      };

      // Get the latest clutch data to ensure we have current eggs
      const latestClutchResponse = await clutchesApi.getById(clutch.id);
      const latestClutch = latestClutchResponse.data;
      const currentEggs = latestClutch?.eggs || [];

      // Add the egg using the update API with current eggs plus new egg
      await clutchesApi.update(clutch.id, {
        date: clutch.date,
        eggs: [
          ...currentEggs.map(egg => ({
            id: egg.id,
            clutchId: egg.clutchId,
            status: egg.status,
            layDate: egg.layDate,
            chickRearMethod: egg.chickRearMethod || null,
            incubationStartDate: egg.incubationStartDate,
            incubationDays: egg.incubationDays,
            bandingNotificationDays: egg.bandingNotificationDays,
            comment: egg.comment,
            hatchingDate: egg.hatchingDate,
            bandingDate: egg.bandingDate,
            bandNumber: egg.bandNumber,
            failureReason: egg.failureReason,
          } as CreateEggDto)), 
          newEgg
        ]
      });

      await onUpdate();
      setIsAddingEgg(false);
      reset();
      toast.success('Egg added successfully');
    } catch (error) {
      console.error('Error adding egg:', error);
      toast.error('Failed to add egg');
    }
  };

  const handleUpdateEgg = async (data: EggFormData) => {
    if (!editingEgg) return;

    try {
      const updatedEgg: CreateEggDto = {
        id: editingEgg.id,
        clutchId: clutch.id,
        status: data.status,
        layDate: data.layDate,
        incubationStartDate: data.incubationStartDate || null,
        incubationDays: data.incubationDays ?? speciesEggDefaults.incubationDays,
        bandingNotificationDays: data.bandingNotificationDays ?? speciesEggDefaults.bandingNotificationDays,
        comment: data.comment || null,
        chickRearMethod: data.chickRearMethod || ChickRearMethod.PARENT_REARED,
        hatchingDate: data.hatchingDate || null,
        bandingDate: data.bandingDate || null,
        bandNumber: data.bandNumber || null,
        failureReason: data.failureReason || null,
      };

      // Get the latest clutch data to ensure we have current eggs
      const latestClutchResponse = await clutchesApi.getById(clutch.id);
      const latestClutch = latestClutchResponse.data;
      const currentEggs = latestClutch?.eggs || [];

      // Update the clutch with the new egg data
      const updatedEggs = currentEggs.map(egg => {
        if (egg.id === editingEgg.id) {
          return updatedEgg;
        }
        return {
          id: egg.id,
          clutchId: egg.clutchId,
          status: egg.status,
          layDate: egg.layDate,
          chickRearMethod: egg.chickRearMethod || null,
          incubationStartDate: egg.incubationStartDate,
          incubationDays: egg.incubationDays,
          bandingNotificationDays: egg.bandingNotificationDays,
          comment: egg.comment,
          hatchingDate: egg.hatchingDate,
          bandingDate: egg.bandingDate,
          bandNumber: egg.bandNumber,
          failureReason: egg.failureReason,
        } as CreateEggDto;
      });

      await clutchesApi.update(clutch.id, {
        eggs: updatedEggs
      });

      await onUpdate();
      setEditingEgg(null);
      reset();
      toast.success('Egg updated successfully');
    } catch (error) {
      console.error('Error updating egg:', error);
      toast.error('Failed to update egg');
    }
  };

  const handleDeleteEgg = async (eggId: string) => {
    try {
      // Get the latest clutch data to ensure we have current eggs
      const latestClutchResponse = await clutchesApi.getById(clutch.id);
      const latestClutch = latestClutchResponse.data;
      const currentEggs = latestClutch?.eggs || [];

      const updatedEggs = currentEggs.filter(egg => egg.id !== eggId).map(egg => ({
        id: egg.id,
        clutchId: egg.clutchId,
        status: egg.status,
        layDate: egg.layDate,
        chickRearMethod: egg.chickRearMethod || null,
        incubationStartDate: egg.incubationStartDate,
        incubationDays: egg.incubationDays,
        bandingNotificationDays: egg.bandingNotificationDays,
        comment: egg.comment,
        hatchingDate: egg.hatchingDate,
        bandingDate: egg.bandingDate,
        bandNumber: egg.bandNumber,
        failureReason: egg.failureReason,
      } as CreateEggDto));
      
      await clutchesApi.update(clutch.id, {
        eggs: updatedEggs
      });

      await onUpdate();
      toast.success('Egg deleted successfully');
    } catch (error) {
      console.error('Error deleting egg:', error);
      toast.error('Failed to delete egg');
    }
  };

  const startEditingEgg = (egg: Egg) => {
    setEditingEgg(egg);
    
    // Helper function to safely format dates
    const formatDateForInput = (dateString: string | null): string => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return '';
        }
        return format(date, 'yyyy-MM-dd');
      } catch (error) {
        console.error('Error formatting date:', dateString, error);
        return '';
      }
    };

    setValue('layDate', formatDateForInput(egg.layDate));
    setValue('status', egg.status);
    setValue('incubationStartDate', formatDateForInput(egg.incubationStartDate));
    setValue('incubationDays', egg.incubationDays ?? speciesEggDefaults.incubationDays);
    setValue('bandingNotificationDays', egg.bandingNotificationDays ?? speciesEggDefaults.bandingNotificationDays);
    setValue('comment', egg.comment || '');
    setValue('chickRearMethod', egg.chickRearMethod || ChickRearMethod.PARENT_REARED);
    setValue('hatchingDate', formatDateForInput(egg.hatchingDate));
    setValue('bandingDate', formatDateForInput(egg.bandingDate));
    setValue('bandNumber', egg.bandNumber || '');
    setValue('failureReason', egg.failureReason || '');
  };

  const startAddingEgg = () => {
    setEditingEgg(null);
    reset({
      layDate: format(new Date(), 'yyyy-MM-dd'),
      status: EggStatus.LAID,
      chickRearMethod: ChickRearMethod.PARENT_REARED,
      incubationDays: speciesEggDefaults.incubationDays,
      bandingNotificationDays: speciesEggDefaults.bandingNotificationDays,
    });
    setIsAddingEgg(true);
  };

  const cancelEditing = () => {
    setEditingEgg(null);
    setIsAddingEgg(false);
    reset();
  };

  const getStatusColor = (status: EggStatus) => {
    switch (status) {
      case EggStatus.LAID:
        return 'bg-cyan-100 text-cyan-800';
      case EggStatus.INCUBATING:
        return 'bg-amber-100 text-amber-800';
      case EggStatus.HATCHED:
        return 'bg-emerald-100 text-emerald-800';
      case EggStatus.NOT_FERTILIZED:
      case EggStatus.BROKEN:
      case EggStatus.ABANDONED:
      case EggStatus.DEAD_IN_SHELL:
      case EggStatus.DEAD_BEFORE_BANDING:
        return 'bg-rose-100 text-rose-800';
      default:
        return 'bg-violet-100 text-violet-800';
    }
  };

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader
        title={`Manage Eggs - ${format(new Date(clutch.date), 'MMMM d, yyyy')}`}
        subtitle={`${clutch.breeding_pair?.male_bird?.bandId || 'Unknown'} × ${clutch.breeding_pair?.female_bird?.bandId || 'Unknown'}`}
        onClose={onClose}
      />
      <BrandModalBody>
                    <div className="mt-2">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-base font-medium text-slate-900">
                          Eggs ({clutch.eggs?.length || 0})
                        </h4>
                        <button
                          type="button"
                          onClick={startAddingEgg}
                          className="inline-flex items-center gap-x-1.5 btn btn-primary text-sm shadow-sm"
                        >
                          <PlusIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                          Add Egg
                        </button>
                      </div>

                      {/* Eggs List */}
                      <div className="space-y-4 max-h-96 overflow-y-auto">
                        {clutch.eggs && clutch.eggs.length > 0 ? (
                          clutch.eggs.map((egg, index) => (
                            <div key={egg.id} className="border rounded-lg p-4 bg-slate-50/80">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-medium text-slate-900">
                                    Egg #{index + 1}
                                  </span>
                                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(egg.status)}`}>
                                    {egg.status.replace(/_/g, ' ')}
                                  </span>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => startEditingEgg(egg)}
                                    className="text-cyan-600 hover:text-indigo-900"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteEgg(egg.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                <div>
                                  <span className="text-slate-500">Laid:</span>
                                  <p>{format(new Date(egg.layDate), 'MMM d, yyyy')}</p>
                                </div>
                                {egg.hatchingDate && (
                                  <div>
                                    <span className="text-slate-500">Hatched:</span>
                                    <p>{format(new Date(egg.hatchingDate), 'MMM d, yyyy')}</p>
                                  </div>
                                )}
                                {egg.bandNumber && (
                                  <div>
                                    <span className="text-slate-500">Band:</span>
                                    <p>{egg.bandNumber}</p>
                                  </div>
                                )}
                                {egg.incubationDays && (
                                  <div>
                                    <span className="text-slate-500">Incubation:</span>
                                    <p>{egg.incubationDays} days</p>
                                  </div>
                                )}
                                {egg.comment && (
                                  <div className="col-span-2 md:col-span-4">
                                    <span className="text-slate-500">Comment:</span>
                                    <p>{egg.comment}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-slate-500 text-center py-8">
                            No eggs recorded for this clutch
                          </p>
                        )}
                      </div>

                      {/* Add/Edit Egg Form */}
                      {(isAddingEgg || editingEgg) && (
                        <div className="mt-6 border-t pt-6">
                          <h4 className="text-base font-medium text-slate-900 mb-4">
                            {editingEgg ? 'Edit Egg' : 'Add New Egg'}
                          </h4>
                          <form onSubmit={handleSubmit(editingEgg ? handleUpdateEgg : handleAddEgg)}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-slate-700">Lay Date</label>
                                <input
                                  type="date"
                                  {...register('layDate')}
                                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                />
                                {errors.layDate && (
                                  <p className="mt-1 text-sm text-red-600">{errors.layDate.message}</p>
                                )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-slate-700">Status</label>
                                <select
                                  {...register('status')}
                                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                >
                                  {Object.values(EggStatus).map((status) => (
                                    <option key={status} value={status}>
                                      {status.replace(/_/g, ' ')}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-slate-700">Incubation Start Date</label>
                                <input
                                  type="date"
                                  {...register('incubationStartDate')}
                                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-slate-700">Incubation Days</label>
                                <input
                                  type="number"
                                  min="0"
                                  max="50"
                                  {...register('incubationDays', { valueAsNumber: true })}
                                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-slate-700">Hatching Date</label>
                                <input
                                  type="date"
                                  {...register('hatchingDate')}
                                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-slate-700">Band Number</label>
                                <input
                                  type="text"
                                  {...register('bandNumber')}
                                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-slate-700">Chick Rear Method</label>
                                <select
                                  {...register('chickRearMethod')}
                                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                >
                                  {Object.values(ChickRearMethod).map((method) => (
                                    <option key={method} value={method}>
                                      {method.replace(/_/g, ' ')}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-slate-700">Banding Notification Days</label>
                                <input
                                  type="number"
                                  min="0"
                                  max="50"
                                  {...register('bandingNotificationDays', { valueAsNumber: true })}
                                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                />
                              </div>

                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-slate-700">Comment</label>
                                <textarea
                                  {...register('comment')}
                                  rows={2}
                                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                />
                              </div>

                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-slate-700">Failure Reason</label>
                                <textarea
                                  {...register('failureReason')}
                                  rows={2}
                                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                />
                              </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                              <button
                                type="button"
                                onClick={cancelEditing}
                                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50/80"
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                className="inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700"
                              >
                                {editingEgg ? 'Update Egg' : 'Add Egg'}
                              </button>
                            </div>
                          </form>
                        </div>
                      )}
                    </div>
      </BrandModalBody>
      <BrandModalFooter>
        <button
          type="button"
          className="btn btn-secondary text-sm"
          onClick={onClose}
        >
          Close
        </button>
      </BrandModalFooter>
    </BrandModal>
  );
} 